// Mobile port of src/lib/trash.ts (web). Same `trash` table, same columns,
// same restore mechanics — byte-compatible so web's Trash/Archive settings
// page (src/app/settings/page.tsx TrashSettingsContent) can see and restore
// anything deleted from mobile, and vice-versa.
//
// Web's `kind` column distinguishes 'trash' (recoverable delete) from
// 'archive' (deliberate archival) — mobile only ever writes 'trash' for now
// (KYRS has no archive-menu-item concept), but the field is threaded through
// so a future archive feature doesn't require a schema change.
import type { SupabaseClient } from '@supabase/supabase-js';

export type TrashKind = 'trash' | 'archive';

export interface TrashItem {
  id: string;
  user_id: string;
  source_component: string;
  item_type: string;
  original_table: string;
  original_id: string;
  item_title: string;
  item_data: Record<string, any>;
  deleted_at: string;
  kind: TrashKind;
}

export async function moveToTrash(
  supabase: SupabaseClient,
  userId: string,
  params: {
    sourceComponent: string;
    itemType: string;
    originalTable: string;
    originalId: string;
    itemTitle: string;
    itemData: Record<string, any>;
    kind?: TrashKind;
  }
) {
  const { error } = await supabase.from('trash').insert({
    user_id: userId,
    source_component: params.sourceComponent,
    item_type: params.itemType,
    original_table: params.originalTable,
    original_id: params.originalId,
    item_title: params.itemTitle,
    item_data: params.itemData,
    kind: params.kind ?? 'trash',
  });
  if (error) {
    // CRITICAL (matches web comment verbatim): throw instead of just
    // logging. Callers wrap this in a try/catch that aborts the delete — if
    // we swallow the error here, the snapshot silently fails and the caller
    // happily deletes the row, leaving the item unrecoverable.
    console.error('moveToTrash error:', error);
    throw new Error(`Failed to move ${params.itemType} to trash: ${error.message}`);
  }
}

export async function restoreFromTrash(supabase: SupabaseClient, trashId: string) {
  const { data: trashItem, error: fetchError } = await supabase
    .from('trash')
    .select('*')
    .eq('id', trashId)
    .single();

  if (fetchError || !trashItem) throw fetchError || new Error('Trash item not found');

  const { item_data, original_table } = trashItem as TrashItem;

  const { error: insertError } = await supabase.from(original_table).insert(item_data);
  if (insertError) throw insertError;

  const { error: deleteError } = await supabase.from('trash').delete().eq('id', trashId);
  if (deleteError) throw deleteError;
}

export async function permanentDelete(supabase: SupabaseClient, trashId: string) {
  const { error } = await supabase.from('trash').delete().eq('id', trashId);
  if (error) throw error;
}

export async function fetchTrash(
  supabase: SupabaseClient,
  userId: string,
  kind: TrashKind = 'trash'
): Promise<TrashItem[]> {
  const { data, error } = await supabase
    .from('trash')
    .select('*')
    .eq('user_id', userId)
    .eq('kind', kind)
    .order('deleted_at', { ascending: false });

  if (error) throw error;
  return (data || []) as TrashItem[];
}

// Mirrors web's handleMoveToOtherKind (src/app/settings/page.tsx
// TrashSettingsContent) exactly: flips a trashed/archived item's `kind`
// without restoring or re-inserting it anywhere.
export async function moveToOtherKind(supabase: SupabaseClient, trashId: string, targetKind: TrashKind) {
  const { error } = await supabase.from('trash').update({ kind: targetKind }).eq('id', trashId);
  if (error) throw error;
}

export async function emptyAllTrash(
  supabase: SupabaseClient,
  userId: string,
  kind: TrashKind = 'trash'
) {
  const { error } = await supabase.from('trash').delete().eq('user_id', userId).eq('kind', kind);
  if (error) throw error;
}
